import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { AppShell } from './components/AppShell';
import { CommandPalette, useCommandPalette } from './components/CommandPalette';
import { OfflineBanner } from './components/OfflineBanner';
import { ClientDetail } from './routes/ClientDetail';
import { Broadcasts } from './routes/Broadcasts';
import { Clients } from './routes/Clients';
import { RoleGuard } from './components/RoleGuard';
import { EmitInvoice } from './routes/EmitInvoice';
import { EmittedInvoice } from './routes/EmittedInvoice';
import { InvoiceDetail } from './routes/InvoiceDetail';
import { InvoiceList } from './routes/InvoiceList';
import { InvoiceReview } from './routes/InvoiceReview';
import { NewWork } from './routes/NewWork';
import { SettingsFiscal } from './routes/SettingsFiscal';
import { Pair } from './routes/Pair';

// Lazy-loaded: only reached at /p/:token (public quote link sent to
// clients, no auth). Keeps the QR signature canvas + ECDH crypto out
// of the main bundle so /pair (Lighthouse-audited cold-start route)
// stays under the perf budget.
const QuotePublic = lazy(() =>
  import('./routes/QuotePublic').then((m) => ({ default: m.QuotePublic })),
);
import {
  Settings,
  SettingsAccount,
  SettingsAppearance,
  SettingsBrandingPanel,
  SettingsIndexRedirect,
  SettingsPrivacy,
  SettingsProfile,
  SettingsSessions,
  SettingsTeam,
} from './routes/Settings';
import { Stats } from './routes/Stats';
import { WorkList } from './routes/WorkList';
import { WorkDetail } from './routes/WorkDetail';
import { clearAll } from './store/db';
import { clearIdentity, getIdentity, type WebIdentity } from './store/keys';
import { migrateLegacyDbIfNeeded } from './store/migration';
import { relay } from './sync/relay';
import { useLiveSync } from './sync/useLiveSync';
import { useFontSize } from './theme/useFontSize';
import { useTheme } from './theme/useTheme';
import './App.css';

/**
 * Top-level router for the spec 200 web client.
 *
 * Three states the app boots into:
 *   - hydrating  → quiet shell while we read IndexedDB.
 *   - unpaired   → /pair (the QR landing).
 *   - paired     → / and /works/:id (the WorkList + WorkDetail).
 *
 * Routing convention: BrowserRouter (HTML5 pushState). app.solvr.es
 * always serves index.html as the SPA fallback (decision Fase 0
 * #1) so deep-links like `/works/abc` survive a refresh.
 *
 * Identity is owned by App, not by the router itself, so a
 * post-pair `onPaired` from /pair drops us back into the routed
 * WorkList without a full page reload. When identity goes null
 * (logout, terminal revoked) we navigate the user back to /pair.
 */
export function App() {
  const [identity, setIdentity] = useState<WebIdentity | null>(null);
  const [hydrated, setHydrated] = useState(false);

  // Theme provider — applies data-theme on <html> based on the
  // persisted preference + prefers-color-scheme. Runs unconditionally
  // (even on /pair) so an unpaired user already gets dark mode if
  // their OS asks for it.
  useTheme();
  // Font-size scaling — applies the class wrapper on <html> so the
  // --typo-* tokens scale globally.
  useFontSize();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Spec 200 Fase 8 — one-shot migration from the legacy
        // single-DB (`cuadrilla` v2) to the split `cuadrilla-data`
        // + `cuadrilla-identity` databases. Idempotent: a flag in
        // localStorage gates the work after the first successful
        // run. Failures here are swallowed so a migration hiccup
        // can't lock the user out — db.ts and keys.ts will open
        // their fresh DBs in that case, the user simply re-pairs.
        try {
          await migrateLegacyDbIfNeeded();
        } catch (err) {
          console.warn('[migration] legacy IDB migration failed:', err);
        }
        const stored = await getIdentity();
        if (!cancelled) setIdentity(stored);
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Live sync — connects the relay singleton when paired, drains
  // incoming messages into IndexedDB, disconnects on logout. The
  // hook is conditionalised on `identity` so an unpaired browser
  // never opens a WebSocket against the relay.
  useLiveSync(identity);

  // Spec 200 Fase 5 #7 — auth-error wipe. The relay surfaces a 401
  // (terminal revoked from mobile, token rotated) via onAuthError;
  // we drop the in-memory identity, wipe IndexedDB (identity row
  // + works/photos/voiceNotes), and the BrowserRouter below
  // redirects the user to /pair on the next render because
  // `identity` is now null.
  useEffect(() => {
    if (!identity) return;
    const off = relay.onAuthError(async () => {
      try {
        await clearIdentity();
        await clearAll();
      } catch {
        // The wipe is best-effort — even if storage is in a weird
        // state, dropping `identity` from React state still pushes
        // the user back to /pair.
      }
      setIdentity(null);
    });
    return off;
  }, [identity]);

  if (!hydrated) {
    return <div className="app-shell" data-testid="app-loading" />;
  }

  return (
    <BrowserRouter
      // Spec 200 Fase 5 hardening — opt in to React Router 6.28's
      // v7-compatible behaviour so the console doesn't get spammed
      // with deprecation warnings AND the upgrade to v7 (when we do
      // it) is a no-op for the routing surface.
      //
      // Only `v7_startTransition` + `v7_relativeSplatPath` are
      // accepted by `<BrowserRouter>` — the data-router flags
      // (v7_fetcherPersist, v7_normalizeFormMethod, etc.) live on
      // `createBrowserRouter`, which we don't use. If we migrate
      // to the data router later, those move with it.
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <OfflineBanner />
      <AppRoutes identity={identity} setIdentity={setIdentity} />
    </BrowserRouter>
  );
}

// Inner component so the Cmd+K hook (which uses `useNavigate`) and
// every other router-aware hook live inside the BrowserRouter
// context. The palette is mounted only when the user is paired —
// it has no useful actions on /pair.
function AppRoutes({
  identity,
  setIdentity,
}: {
  identity: WebIdentity | null;
  setIdentity: (i: WebIdentity | null) => void;
}) {
  const [paletteOpen, setPaletteOpen] = useCommandPalette();

  // Public quote route — recipient's browser opens /p/<token> with no
  // pairing. Bypasses both the identity gate and the AppShell so the
  // page is decoupled from the authenticated app chrome (spec 236 PR9).
  const location = useLocation();
  if (location.pathname.startsWith('/p/')) {
    return (
      <Suspense fallback={null}>
        <Routes>
          <Route path="/p/:token" element={<QuotePublic />} />
          <Route path="*" element={<Navigate to="/pair" replace />} />
        </Routes>
      </Suspense>
    );
  }

  if (!identity) {
    return (
      <Routes>
        <Route path="/pair" element={<Pair onPaired={(id) => setIdentity(id)} />} />
        <Route path="*" element={<Navigate to="/pair" replace />} />
      </Routes>
    );
  }
  // R1 master-detail shell wraps every paired route. Below 1280 px
  // the shell collapses to single-column and each route's own
  // header takes over — no behavioural change for mobile.
  return (
    <AppShell identity={identity}>
      <Routes>
        <Route path="/" element={<WorkList identity={identity} />} />
        <Route path="/works/new" element={<NewWork identity={identity} />} />
        <Route path="/works/:workId" element={<WorkDetail identity={identity} />} />
        <Route path="/stats" element={<Stats identity={identity} />} />
        <Route
          path="/clients"
          element={
            <RoleGuard identity={identity} allow={['creator', 'admin']} feature="Clientes">
              <Clients identity={identity} />
            </RoleGuard>
          }
        />
        <Route
          path="/clients/:slug"
          element={
            <RoleGuard identity={identity} allow={['creator', 'admin']} feature="Clientes">
              <ClientDetail identity={identity} />
            </RoleGuard>
          }
        />
        <Route
          path="/broadcasts"
          element={
            <RoleGuard identity={identity} allow={['creator', 'admin']} feature="Avisos del equipo">
              <Broadcasts identity={identity} />
            </RoleGuard>
          }
        />
        <Route path="/invoices" element={<InvoiceList identity={identity} />} />
        <Route
          path="/invoices/new"
          element={
            <RoleGuard identity={identity} allow={['creator', 'admin']} feature="Emitir facturas">
              <EmitInvoice identity={identity} />
            </RoleGuard>
          }
        />
        <Route
          path="/invoices/new/review"
          element={
            <RoleGuard identity={identity} allow={['creator', 'admin']} feature="Emitir facturas">
              <InvoiceReview identity={identity} />
            </RoleGuard>
          }
        />
        <Route
          path="/invoices/new/emitted"
          element={
            <RoleGuard identity={identity} allow={['creator', 'admin']} feature="Emitir facturas">
              <EmittedInvoice identity={identity} />
            </RoleGuard>
          }
        />
        <Route path="/invoices/:id" element={<InvoiceDetail identity={identity} />} />
        <Route
          path="/settings"
          element={<Settings identity={identity} onLogout={() => setIdentity(null)} />}
        >
          {/* Index: desktop redirects to /perfil; mobile renders the
              index list inline (the layout handles that branch). */}
          <Route index element={<SettingsIndexRedirect />} />
          <Route path="perfil" element={<SettingsProfile identity={identity} />} />
          <Route path="equipo" element={<SettingsTeam identity={identity} />} />
          <Route path="apariencia" element={<SettingsAppearance />} />
          <Route
            path="identidad"
            element={<SettingsBrandingPanel identity={identity} />}
          />
          {/* Legacy slug — kept so any bookmarked URL or stale e2e
              link still lands on the new panel. */}
          <Route
            path="datos-fiscales"
            element={<Navigate to="/settings/identidad" replace />}
          />
          <Route path="privacidad" element={<SettingsPrivacy />} />
          <Route
            path="sesiones-web"
            element={<SettingsSessions identity={identity} />}
          />
          <Route
            path="cuenta"
            element={
              <SettingsAccount
                identity={identity}
                onLogout={() => setIdentity(null)}
              />
            }
          />
        </Route>
        {/* Spec 218 PR4 — fiscal profile editor is required to emit
            invoices, so we expose the page once the route flow lands. */}
        <Route
          path="/settings/fiscal"
          element={
            <RoleGuard identity={identity} allow={['creator', 'admin']} feature="Datos fiscales">
              <SettingsFiscal identity={identity} />
            </RoleGuard>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </AppShell>
  );
}
